import { useEffect, useState, useMemo } from "react";
import { Navigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Sparkles, LogOut, Trash2, Search, Mail, Users, Inbox, CheckSquare, TrendingUp, X } from "lucide-react";
import Logo from "@/components/Logo";
import { toast } from "sonner";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const STATUS_COLORS = {
  new: "bg-[#00D4FF]/10 text-[#00D4FF] border-[#00D4FF]/30",
  contacted: "bg-[#7C3AED]/10 text-[#a78bfa] border-[#7C3AED]/30",
  qualified: "bg-[#2563EB]/10 text-[#60a5fa] border-[#2563EB]/30",
  closed: "bg-white/5 text-gray-400 border-white/10",
};

function StatCard({ icon: Icon, label, value, tint = "cyan" }) {
  const tints = {
    cyan: "text-[#00D4FF] bg-[#00D4FF]/10 border-[#00D4FF]/30",
    blue: "text-[#60a5fa] bg-[#2563EB]/10 border-[#2563EB]/30",
    purple: "text-[#a78bfa] bg-[#7C3AED]/10 border-[#7C3AED]/30",
    gray: "text-gray-400 bg-white/5 border-white/10",
  };
  return (
    <div className="p-5 rounded-2xl bg-[#0B1224] border border-white/10">
      <div className={`w-9 h-9 rounded-lg border flex items-center justify-center ${tints[tint]}`}>
        <Icon className="w-4.5 h-4.5"/>
      </div>
      <div className="mt-4 font-display text-3xl font-bold">{value}</div>
      <div className="mt-1 text-xs uppercase tracking-[0.2em] text-gray-500">{label}</div>
    </div>
  );
}

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [selectedLead, setSelectedLead] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);

  useEffect(() => {
    if (user && user !== false && user !== null) fetchData();
  }, [user]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [{ data: leadsData }, { data: statsData }] = await Promise.all([
        api.get("/leads"),
        api.get("/admin/stats"),
      ]);
      setLeads(leadsData);
      setStats(statsData);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed to load");
    } finally { setLoading(false); }
  };

  const updateStatus = async (id, status) => {
    try {
      const { data } = await api.patch(`/leads/${id}`, { status });
      setLeads(prev => prev.map(l => l.id === id ? data : l));
      if (selectedLead?.id === id) setSelectedLead(data);
      toast.success(`Marked as ${status}`);
      fetchData();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Update failed");
    }
  };

  const deleteLead = async (id) => {
    try {
      await api.delete(`/leads/${id}`);
      setLeads(prev => prev.filter(l => l.id !== id));
      setConfirmDelete(null);
      if (selectedLead?.id === id) setSelectedLead(null);
      toast.success("Lead deleted");
      fetchData();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Delete failed");
    }
  };

  const filtered = useMemo(() => {
    const term = q.toLowerCase();
    return leads.filter(l => {
      const matchesQ = !term ||
        l.full_name.toLowerCase().includes(term) ||
        l.email.toLowerCase().includes(term) ||
        l.company.toLowerCase().includes(term) ||
        (l.country || "").toLowerCase().includes(term);
      const matchesStatus = statusFilter === "all" || l.status === statusFilter;
      return matchesQ && matchesStatus;
    });
  }, [leads, q, statusFilter]);

  if (user === null) {
    return <div className="min-h-screen bg-[#050816] text-gray-400 flex items-center justify-center">Loading...</div>;
  }
  if (user === false) return <Navigate to="/admin/login" replace />;

  return (
    <div className="min-h-screen bg-[#050816] text-white flex">
      <aside className="hidden lg:flex flex-col w-64 shrink-0 border-r border-white/10 bg-[#040611] p-6">
        <Link to="/" className="block">
          <Logo size="sm" />
        </Link>
        <nav className="mt-10 space-y-1 flex-1">
          <div className="px-3 py-2 rounded-md bg-white/5 text-sm font-medium flex items-center gap-2">
            <Inbox className="w-4 h-4 text-[#00D4FF]"/> Leads
          </div>
        </nav>
        <div className="mt-auto pt-6 border-t border-white/10">
          <div className="text-xs text-gray-500">Signed in as</div>
          <div className="mt-1 text-sm">{user.email}</div>
          <Button
            variant="ghost"
            onClick={logout}
            data-testid="admin-logout"
            className="mt-3 w-full justify-start text-gray-300 hover:text-white hover:bg-white/5"
          >
            <LogOut className="w-4 h-4 mr-2"/> Logout
          </Button>
        </div>
      </aside>

      <main className="flex-1 min-w-0">
        <header className="h-16 border-b border-white/10 flex items-center justify-between px-6 lg:px-10">
          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-gray-500">Dashboard</div>
            <div className="font-display text-lg font-semibold">Leads Overview</div>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/" className="text-sm text-gray-400 hover:text-white">View Site →</Link>
            <Button onClick={logout} data-testid="admin-logout-top" variant="ghost" className="lg:hidden text-gray-300"><LogOut className="w-4 h-4"/></Button>
          </div>
        </header>

        <div className="p-6 lg:p-10">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <StatCard icon={Users} label="Total Leads" value={stats?.total_leads ?? "–"} tint="cyan"/>
            <StatCard icon={Inbox} label="New" value={stats?.new ?? "–"} tint="blue"/>
            <StatCard icon={TrendingUp} label="Qualified" value={stats?.qualified ?? "–"} tint="purple"/>
            <StatCard icon={Mail} label="Subscribers" value={stats?.newsletter_subscribers ?? "–"} tint="gray"/>
          </div>

          <div className="mt-8 flex flex-col md:flex-row md:items-center gap-3 justify-between">
            <div className="relative w-full md:max-w-sm">
              <Search className="w-4 h-4 text-gray-500 absolute left-3 top-1/2 -translate-y-1/2"/>
              <Input
                value={q}
                onChange={(e)=>setQ(e.target.value)}
                data-testid="admin-search"
                placeholder="Search by name, email, company, country..."
                className="pl-9 bg-[#0B1224] border-white/10 h-10"
              />
            </div>
            <div className="flex items-center gap-3">
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger data-testid="admin-status-filter" className="w-40 bg-[#0B1224] border-white/10 text-white h-10">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#0B1224] border-white/10 text-white">
                  <SelectItem value="all">All statuses</SelectItem>
                  <SelectItem value="new">New</SelectItem>
                  <SelectItem value="contacted">Contacted</SelectItem>
                  <SelectItem value="qualified">Qualified</SelectItem>
                  <SelectItem value="closed">Closed</SelectItem>
                </SelectContent>
              </Select>
              <Button onClick={fetchData} data-testid="admin-refresh" variant="secondary" className="bg-white/5 hover:bg-white/10 border border-white/10 h-10">
                Refresh
              </Button>
            </div>
          </div>

          <div className="mt-6 rounded-2xl bg-[#0B1224] border border-white/10 overflow-hidden">
            <div className="overflow-x-auto thin-scrollbar">
              <table className="w-full text-sm min-w-[900px]">
                <thead className="border-b border-white/10 text-xs uppercase tracking-[0.15em] text-gray-500">
                  <tr>
                    <th className="text-left px-5 py-4 font-medium">Name</th>
                    <th className="text-left px-5 py-4 font-medium">Company</th>
                    <th className="text-left px-5 py-4 font-medium">Email</th>
                    <th className="text-left px-5 py-4 font-medium">Country</th>
                    <th className="text-left px-5 py-4 font-medium">Interest</th>
                    <th className="text-left px-5 py-4 font-medium">Status</th>
                    <th className="text-left px-5 py-4 font-medium">Received</th>
                    <th className="text-right px-5 py-4 font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/5">
                  {loading ? (
                    <tr><td colSpan={8} className="px-5 py-10 text-center text-gray-500" data-testid="admin-loading">Loading leads...</td></tr>
                  ) : filtered.length === 0 ? (
                    <tr><td colSpan={8} className="px-5 py-14 text-center text-gray-500" data-testid="admin-empty">No leads found.</td></tr>
                  ) : filtered.map((l) => (
                    <tr key={l.id} className="hover:bg-white/[0.02]" data-testid={`admin-lead-row-${l.id}`}>
                      <td className="px-5 py-4">
                        <button onClick={()=>setSelectedLead(l)} className="text-white hover:text-[#00D4FF] font-medium">
                          {l.full_name}
                        </button>
                        <div className="text-xs text-gray-500">{l.phone}</div>
                      </td>
                      <td className="px-5 py-4">{l.company}</td>
                      <td className="px-5 py-4"><a href={`mailto:${l.email}`} className="hover:text-[#00D4FF]">{l.email}</a></td>
                      <td className="px-5 py-4">{l.country}</td>
                      <td className="px-5 py-4">{l.service_interest || "-"}</td>
                      <td className="px-5 py-4">
                        <span className={`inline-block px-2.5 py-1 rounded-full text-xs border ${STATUS_COLORS[l.status] || STATUS_COLORS.closed}`}>
                          {l.status}
                        </span>
                      </td>
                      <td className="px-5 py-4 text-gray-400 whitespace-nowrap">
                        {new Date(l.created_at).toLocaleDateString(undefined,{ year:"numeric", month:"short", day:"numeric" })}
                      </td>
                      <td className="px-5 py-4">
                        <div className="flex items-center justify-end gap-1">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={()=>setSelectedLead(l)}
                            data-testid={`admin-view-${l.id}`}
                            className="text-gray-300 hover:text-white hover:bg-white/5"
                          >
                            View
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={()=>setConfirmDelete(l)}
                            data-testid={`admin-delete-${l.id}`}
                            className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                          >
                            <Trash2 className="w-4 h-4"/>
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </main>

      {/* Lead detail dialog */}
      <Dialog open={!!selectedLead} onOpenChange={(v)=>!v && setSelectedLead(null)}>
        <DialogContent className="max-w-2xl bg-[#0B1224] border-white/10 text-white" data-testid="admin-lead-detail">
          {selectedLead && (
            <>
              <DialogHeader>
                <div className="overline">Lead Detail</div>
                <DialogTitle className="font-display text-2xl font-bold">{selectedLead.full_name}</DialogTitle>
              </DialogHeader>
              <div className="grid grid-cols-2 gap-4 mt-2 text-sm">
                <div><div className="text-xs text-gray-500 uppercase tracking-wider">Company</div><div className="mt-1">{selectedLead.company}</div></div>
                <div><div className="text-xs text-gray-500 uppercase tracking-wider">Country</div><div className="mt-1">{selectedLead.country}</div></div>
                <div><div className="text-xs text-gray-500 uppercase tracking-wider">Email</div><div className="mt-1"><a href={`mailto:${selectedLead.email}`} className="hover:text-[#00D4FF]">{selectedLead.email}</a></div></div>
                <div><div className="text-xs text-gray-500 uppercase tracking-wider">Phone</div><div className="mt-1">{selectedLead.phone}</div></div>
                <div className="col-span-2"><div className="text-xs text-gray-500 uppercase tracking-wider">Interested In</div><div className="mt-1">{selectedLead.service_interest || "-"}</div></div>
                <div className="col-span-2"><div className="text-xs text-gray-500 uppercase tracking-wider">Message</div><div className="mt-1 text-gray-300 whitespace-pre-wrap">{selectedLead.message || "-"}</div></div>
              </div>
              <div className="mt-6 flex items-center justify-between border-t border-white/10 pt-4">
                <div className="flex items-center gap-2 text-xs text-gray-500">
                  Status:
                  <Select value={selectedLead.status} onValueChange={(v)=>updateStatus(selectedLead.id, v)}>
                    <SelectTrigger data-testid="admin-detail-status" className="w-36 bg-black/30 border-white/10 h-9 text-white">
                      <SelectValue/>
                    </SelectTrigger>
                    <SelectContent className="bg-[#0B1224] border-white/10 text-white">
                      <SelectItem value="new">New</SelectItem>
                      <SelectItem value="contacted">Contacted</SelectItem>
                      <SelectItem value="qualified">Qualified</SelectItem>
                      <SelectItem value="closed">Closed</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <Button onClick={()=>setSelectedLead(null)} variant="ghost" className="text-gray-400"><X className="w-4 h-4 mr-1"/> Close</Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete confirm */}
      <AlertDialog open={!!confirmDelete} onOpenChange={(v)=>!v && setConfirmDelete(null)}>
        <AlertDialogContent className="bg-[#0B1224] border-white/10 text-white">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this lead?</AlertDialogTitle>
            <AlertDialogDescription className="text-gray-400">
              This action can't be undone. The lead will be permanently removed.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-white/5 border-white/10 text-white hover:bg-white/10">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={()=>deleteLead(confirmDelete.id)}
              data-testid="admin-delete-confirm"
              className="bg-red-600 hover:bg-red-500 text-white"
            >Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
